/**
 * Shared scaffolding for vertical-domain assistant panels.
 *
 * Every domain panel (network, process, orgchart, flowchart) re-implements
 * the same sub-panel state machine: open/close, forward/back navigation,
 * slide-in animation classes, keyboard shortcut binding, and the global
 * `assistant:collapse` / `assistant:expand` event listeners. This hook
 * centralizes that scaffolding so panels become composition over chrome
 * rather than 250 lines of duplicated state plumbing.
 *
 * Behavior parity with the per-panel implementations:
 *   - `navigateToSubPanel(p)` sets direction='forward' + activeSubPanel=p
 *   - `navigateBack()` sets direction='back' + activeSubPanel=null
 *   - `getAnimationClass(isSubPanel)` returns the matching tailwind
 *     `animate-in slide-in-from-{right,left}-4` class, OR the empty
 *     string when `prefers-reduced-motion: reduce` is active (a11y).
 *   - Keyboard shortcuts wire `quickActions` keys, ignoring inputs/
 *     textareas/contenteditable so users can type without firing them.
 *
 * Generic over the panel-id union so each domain keeps its own type
 * (e.g. `'map' | 'inspect' | 'review'` for network).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AssistantView } from '../interfaces/IAssistantContext';
import { ROOT_VIEW } from '../interfaces/IAssistantContext';

export interface AssistantQuickAction {
  id: string;
  label: string;
  /** Single-character keyboard shortcut (e.g. 'm', 'i'). */
  key?: string;
  icon?: React.ReactNode;
  action: () => void | Promise<void>;
}

export type NavDirection = 'forward' | 'back' | 'none';

export interface UseAssistantPanelNavOptions<TPanel extends string> {
  /** Quick-action list whose keys bind to keyboard shortcuts when open. */
  quickActions: AssistantQuickAction[];
  /**
   * Optional: react to onboarding-driven panel jumps (e.g. open the panel
   * and navigate straight to a specific sub-panel).
   */
  onboardingTarget?: TPanel | null;
}

export interface UseAssistantPanelNavResult<TPanel extends string> {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeSubPanel: TPanel | null;
  navigationStack: AssistantView[];
  currentView: AssistantView;
  navDirection: NavDirection;
  executingActionId: string | null;
  navigateToSubPanel: (panel: TPanel) => void;
  navigateBack: () => void;
  pushView: (view: AssistantView) => void;
  popView: () => void;
  resetNavigation: () => void;
  getAnimationClass: (isSubPanel: boolean) => string;
  executeQuickAction: (id: string, action: () => void) => void;
}

function isTypingInInput(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as Element;
  const tag = el.tagName?.toLowerCase?.() ?? '';
  if (tag === 'input' || tag === 'textarea') return true;
  return el.getAttribute?.('contenteditable') === 'true';
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useAssistantPanelNav<TPanel extends string>({
  quickActions,
  onboardingTarget,
}: UseAssistantPanelNavOptions<TPanel>): UseAssistantPanelNavResult<TPanel> {
  const [isOpen, setIsOpen] = useState(false);
  const [navigationStack, setNavigationStack] = useState<AssistantView[]>([ROOT_VIEW]);
  const [navDirection, setNavDirection] = useState<NavDirection>('none');
  const [executingActionId, setExecutingActionId] = useState<string | null>(null);

  const currentView = navigationStack[navigationStack.length - 1] ?? ROOT_VIEW;
  const activeSubPanel = currentView.id === ROOT_VIEW.id ? null : (currentView.id as TPanel);

  const pushView = useCallback((view: AssistantView) => {
    setNavDirection('forward');
    setNavigationStack((stack) => {
      const root = stack[0] ?? ROOT_VIEW;
      return [root, { ...view, parentId: root.id }];
    });
  }, []);

  const popView = useCallback(() => {
    setNavDirection('back');
    setNavigationStack((stack) => (stack.length <= 1 ? stack : stack.slice(0, 1)));
  }, []);

  const resetNavigation = useCallback(() => {
    setNavDirection('none');
    setNavigationStack([ROOT_VIEW]);
  }, []);

  const navigateToSubPanel = useCallback(
    (panel: TPanel) => {
      pushView({ id: panel, title: panel });
    },
    [pushView],
  );

  const navigateBack = popView;

  const getAnimationClass = useCallback(
    (isSubPanel: boolean): string => {
      if (navDirection === 'none') return '';
      if (prefersReducedMotion()) return '';
      if (isSubPanel) {
        return navDirection === 'forward'
          ? 'animate-in slide-in-from-right-4 duration-200 ease-out'
          : '';
      }
      return navDirection === 'back'
        ? 'animate-in slide-in-from-left-4 duration-200 ease-out'
        : '';
    },
    [navDirection],
  );

  const executeQuickAction = useCallback(
    (id: string, action: () => void | Promise<void>) => {
      if (executingActionId) return;
      setExecutingActionId(id);
      // 150ms matches the visual feedback window per-panel implementations
      // converged on; long enough to read as a press, short enough to not
      // gate rapid keypresses. Async actions hold the executing state until
      // they settle so loading affordances stay accurate.
      const finish = () => setTimeout(() => setExecutingActionId(null), 150);
      try {
        const result = action();
        if (result && typeof (result as Promise<void>).then === 'function') {
          void (result as Promise<void>).finally(finish);
        } else {
          finish();
        }
      } catch {
        finish();
      }
    },
    [executingActionId],
  );

  // Onboarding-driven entry: open the panel and route to the requested
  // sub-panel. Effect runs once per target value change.
  useEffect(() => {
    if (!onboardingTarget) return;
    setIsOpen(true);
    pushView({ id: onboardingTarget, title: onboardingTarget });
  }, [onboardingTarget, pushView]);

  // Keyboard shortcuts. Capture phase so the panel claims keys before
  // canvas/document handlers; the input-focus guard keeps typing safe.
  // Keyed on the action id so a re-render that swaps action references
  // (without changing semantic ids) doesn't churn the listener.
  const actionIndex = useMemo(() => {
    const map = new Map<string, AssistantQuickAction>();
    for (const a of quickActions) {
      if (a.key) map.set(a.key, a);
    }
    return map;
  }, [quickActions]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (isTypingInInput(e.target)) return;
      if (executingActionId) return;
      const action = actionIndex.get(e.key);
      if (!action) return;
      e.preventDefault();
      e.stopPropagation();
      executeQuickAction(action.id, action.action);
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [isOpen, actionIndex, executingActionId, executeQuickAction]);

  // Global assistant open/close events — the canvas chrome dispatches these
  // when the user clicks a toolbar Assistant icon, presses a global hotkey,
  // or completes an onboarding flow.
  useEffect(() => {
    const onCollapse = () => {
      setIsOpen(false);
      resetNavigation();
    };
    const onExpand = () => {
      setIsOpen(true);
      resetNavigation();
    };
    window.addEventListener('assistant:collapse', onCollapse);
    window.addEventListener('assistant:expand', onExpand);
    return () => {
      window.removeEventListener('assistant:collapse', onCollapse);
      window.removeEventListener('assistant:expand', onExpand);
    };
  }, [resetNavigation]);

  return {
    isOpen,
    setIsOpen,
    activeSubPanel,
    navigationStack,
    currentView,
    navDirection,
    executingActionId,
    navigateToSubPanel,
    navigateBack,
    pushView,
    popView,
    resetNavigation,
    getAnimationClass,
    executeQuickAction,
  };
}
